/**
 * The organization's three dialogs — `organization-build-book.md` §6.11,
 * `org-11-dialogs.png`.
 *
 * **Add** makes a place. **Move** changes who can reach one, which is why it
 * shows what that will cost before it does it. **Grant access** gives one
 * person one role at one place, and is the same component whether it is
 * opened from a unit's Access tab or from a person's card — one screen for
 * one idea, so the two cannot drift apart.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  MeResponse, MoveConsequence, OrgUnitDetail, OrgUnitKind, OrgUnitRow, PermissionMatrix,
} from '@rowted/shared'
import { can, permissionsAt } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { request } from '../../lib/api.js'
import { KindTile } from '../../builder/kind-tile.js'
import { Avatar, Band, Button, Tag } from '../primitives.js'
import { Modal } from '../overlays.js'
import {
  CheckRow, ComboInput, Field, SelectInput, TextArea, TextInput, type ComboItem,
} from '../form/index.js'
import { IconNavigate, IconWarn, RECORD_ICONS } from '../../frame/icons.js'

/** What a kind may hold. The database says the same thing in a trigger (0075). */
const MAY_HOLD: Record<string, OrgUnitKind[]> = {
  company: ['region', 'district', 'branch'],
  region: ['district', 'branch'],
  district: ['branch'],
  branch: [],
}

const KIND_BLURB: Record<string, string> = {
  region: 'groups districts',
  district: 'groups branches',
  branch: 'where work happens',
}

// ---------------------------------------------------------------------------

export function AddUnitDialog({ parent, units, kind: wanted, onClose, onAdded }: {
  parent: OrgUnitRow
  units: OrgUnitRow[]
  /** Preselected by "Add region" / "Add branch" in the page bar. */
  kind?: OrgUnitKind
  onClose: () => void
  onAdded: (unit: OrgUnitDetail) => void
}) {
  const offered = MAY_HOLD[parent.kind] ?? []
  const [kind, setKind] = useState<OrgUnitKind>(
    wanted && offered.includes(wanted) ? wanted : offered[offered.length - 1] ?? 'branch')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [codeTouched, setCodeTouched] = useState(false)
  const [parentId, setParentId] = useState(parent.id)
  const [seedYard, setSeedYard] = useState(true)
  const [copyFrom, setCopyFrom] = useState<string | null>(null)
  const [grantManager, setGrantManager] = useState(true)
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const chosenParent = units.find((one) => one.id === parentId) ?? parent

  /*
   * The code writes itself from the name until somebody types one. A branch
   * called "York" is YORK, "Lehigh Valley" is LEVA — first letters where
   * there are several words, the first four where there is one.
   */
  useEffect(() => {
    if (codeTouched) return
    setCode(suggestCode(name))
  }, [name, codeTouched])

  const sibling = units.find((one) => one.parentId === parentId && one.kind === kind)

  async function add() {
    setBusy(true)
    setError(null)
    try {
      const made = await request<OrgUnitDetail>('/org-units', {
        method: 'POST',
        body: {
          name, code, kind, parentId,
          seedYard: kind === 'branch' && seedYard,
          copyFromId: copyFrom,
          grantParentManager: grantManager,
        },
      })
      onAdded(made)
    } catch (problem) {
      const body = problem as { field?: string; message?: string }
      setError({ field: body.field, message: body.message ?? 'That did not save.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Add to the organization"
      width={520}
      footerNote="Nothing is scheduled here until a crew is based here."
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" disabled={busy || !name.trim()} onClick={() => void add()}>
            {busy ? 'Adding…' : `Add ${kind}`}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3.5">
        <span className="text-[12.5px] text-text-muted">Under {chosenParent.name}</span>

        <Field
          label="What is it"
          group
          hint="a region can only hold districts and branches; a district only branches"
        >
          {({ labelId }) => (
            <div role="radiogroup" aria-labelledby={labelId} className="flex gap-2.5">
              {offered.map((one) => (
                <button
                  key={one}
                  type="button"
                  role="radio"
                  aria-checked={one === kind}
                  onClick={() => setKind(one)}
                  className={cn('flex flex-1 flex-col items-start gap-1.5 rounded-[12px] border-[1.5px]',
                    'p-3 text-left',
                    one === kind
                      ? 'border-accent-line bg-green-band-bg'
                      : 'border-line bg-surface hover:bg-hover')}
                >
                  <KindTile kind={one} size={30} />
                  <b className="text-[13px] text-text">{one[0]!.toUpperCase() + one.slice(1)}</b>
                  <span className="text-[11.5px] text-text-muted">{KIND_BLURB[one]}</span>
                </button>
              ))}
            </div>
          )}
        </Field>

        <div className="flex items-start gap-3">
          <Field
            label="Name"
            required
            className="flex-1"
            error={error?.field === 'name' ? error.message : undefined}
          >
            {({ id, invalid }) => (
              <TextInput
                id={id}
                invalid={invalid}
                autoFocus
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            )}
          </Field>

          <Field
            label="Short code"
            required
            className="w-[120px] shrink-0"
            error={error?.field === 'code' ? error.message : undefined}
          >
            {({ id, invalid }) => (
              <TextInput
                id={id}
                invalid={invalid}
                value={code}
                onChange={(event) => {
                  setCodeTouched(true)
                  setCode(event.target.value.toUpperCase())
                }}
              />
            )}
          </Field>
        </div>

        {/* The trail, but only when it says more than the box already does. */}
        <Field
          label="Under"
          hint={pathOf(units, parentId).includes('›') ? pathOf(units, parentId) : undefined}
        >
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              describedBy={describedBy}
              label="Under"
              value={parentId}
              onChange={(value) => value && setParentId(value)}
              options={units
                .filter((one) => (MAY_HOLD[one.kind] ?? []).includes(kind))
                .map((one) => ({ value: one.id, label: one.name }))}
            />
          )}
        </Field>

        <div className="flex flex-col gap-1">
          <span className="text-[11.5px] text-text-muted">Start with</span>
          {kind === 'branch' && (
            <CheckRow
              label="A default yard at the branch address"
              sub="stock has to come off somewhere before a visit can close"
              checked={seedYard}
              onChange={setSeedYard}
            />
          )}
          {sibling && (
            <CheckRow
              label={`Copy ${sibling.name}'s settings and zones`}
              sub="time zone and service zones — never people"
              checked={copyFrom === sibling.id}
              onChange={(on) => setCopyFrom(on ? sibling.id : null)}
            />
          )}
          <CheckRow
            label={`Grant ${chosenParent.name}'s manager access automatically`}
            sub="somebody has to be able to open it"
            checked={grantManager}
            onChange={setGrantManager}
          />
        </div>

        {error && !error.field && (
          <p className="m-0 text-[12.5px] text-danger">{error.message}</p>
        )}
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------

export function MoveUnitDialog({ unit, units, ontoId, onClose, onMoved }: {
  unit: OrgUnitRow
  units: OrgUnitRow[]
  /** Pre-filled when the move came from a drag. */
  ontoId?: string
  onClose: () => void
  onMoved: (unit: OrgUnitDetail) => void
}) {
  const [parentId, setParentId] = useState<string | null>(ontoId ?? null)
  const [preview, setPreview] = useState<MoveConsequence | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  /* Never itself, never a branch, and never anywhere inside its own subtree. */
  const candidates = useMemo(() => {
    const inside = new Set<string>([unit.id])
    let grew = true
    while (grew) {
      grew = false
      for (const one of units) {
        if (one.parentId && inside.has(one.parentId) && !inside.has(one.id)) {
          inside.add(one.id)
          grew = true
        }
      }
    }
    return units.filter((one) => !inside.has(one.id)
      && (MAY_HOLD[one.kind] ?? []).includes(unit.kind))
  }, [units, unit.id, unit.kind])

  useEffect(() => {
    if (!parentId) { setPreview(null); return }
    let live = true
    void request<MoveConsequence>(`/org-units/${unit.id}/move?preview=1`,
      { method: 'POST', body: { parentId } })
      .then((answer) => { if (live) { setPreview(answer); setError(null) } })
      .catch((problem: { message?: string }) => {
        if (live) setError(problem.message ?? 'That move is not allowed.')
      })
    return () => { live = false }
  }, [parentId, unit.id])

  async function move() {
    if (!parentId) return
    setBusy(true)
    setError(null)
    try {
      onMoved(await request<OrgUnitDetail>(`/org-units/${unit.id}/move`,
        { method: 'POST', body: { parentId } }))
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That move did not happen.')
    } finally {
      setBusy(false)
    }
  }

  const target = units.find((one) => one.id === parentId)

  return (
    <Modal
      open
      onClose={onClose}
      title={`Move ${unit.name}`}
      width={520}
      footerNote="Recorded in the audit log."
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" disabled={busy || !parentId} onClick={() => void move()}>
            <IconNavigate size={15} />
            {target ? `Move to ${target.name}` : 'Move'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <span className="text-[12.5px] text-text-muted">Choose its new parent</span>

        <div className="flex max-h-[240px] flex-col overflow-y-auto rounded-[12px] border border-line py-1.5">
          {candidates.map((one) => {
            const Icon = RECORD_ICONS[one.kind]
            return (
              <button
                key={one.id}
                type="button"
                onClick={() => setParentId(one.id)}
                className={cn('mx-1.5 flex h-[38px] items-center gap-2.5 rounded-[9px] px-2.5',
                  'text-left', one.id === parentId ? 'bg-accent-soft' : 'hover:bg-hover')}
              >
                <span className="shrink-0 text-text-muted"><Icon size={15} /></span>
                <span className="truncate text-[13px] font-semibold text-text">{one.name}</span>
                <span className="ml-auto shrink-0 text-[11.5px] text-text-faint">{one.code}</span>
              </button>
            )
          })}
          {candidates.length === 0 && (
            <p className="m-0 px-3 py-2 text-[12.5px] text-text-faint">
              There is nowhere else a {unit.kind} can sit.
            </p>
          )}
        </div>

        {preview && (
          <Band tone="amber" title="What changes" icon={<IconWarn size={15} />}>
            Grants made <b className="font-bold">on {unit.name}</b> ({preview.comesAlong}{' '}
            {preview.comesAlong === 1 ? 'person' : 'people'}) come along.
            {preview.loses.length > 0 && (
              <> {namesOf(preview.loses)} stop reaching {unit.name}.</>
            )}
            {preview.gains.length > 0 && (
              <> {namesOf(preview.gains)} start to.</>
            )}
            {' '}Nothing about visits, inventory or history moves.
          </Band>
        )}

        {error && <p className="m-0 text-[12.5px] text-danger">{error}</p>}
      </div>
    </Modal>
  )
}

const namesOf = (people: { id: string; name: string; roleName: string }[]): string => {
  const shown = people.slice(0, 3).map((one) => one.name)
  const rest = people.length - shown.length
  return rest > 0 ? `${shown.join(', ')} and ${rest} more` : shown.join(', ')
}

// ---------------------------------------------------------------------------

interface RoleRow { id: string; name: string; permissions?: PermissionMatrix }

export function GrantAccessDialog({ me, atUnitId, person, onClose, onGranted }: {
  me: MeResponse
  /** Where the grant applies, when the dialog was opened from a place. */
  atUnitId?: string
  /** Who it is for, when it was opened from a person. */
  person?: { id: string; name: string }
  onClose: () => void
  onGranted: () => void
}) {
  const [who, setWho] = useState<ComboItem | null>(
    person ? { id: person.id, name: person.name } : null)
  const [roleId, setRoleId] = useState<string | null>(null)
  const [unitId, setUnitId] = useState<string | null>(atUnitId ?? null)
  const [note, setNote] = useState('')
  const [roles, setRoles] = useState<RoleRow[]>([])
  const [units, setUnits] = useState<OrgUnitRow[]>([])
  const [error, setError] = useState<{ field?: string; message: string } | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void request<{ items: RoleRow[] }>('/roles')
      .then(({ items }) => setRoles(items))
      .catch(() => undefined)
    void request<{ items: OrgUnitRow[] }>('/org-units')
      .then(({ items }) => setUnits(items.filter((one) => one.reachable && one.active)))
      .catch(() => undefined)
  }, [])

  const unit = units.find((one) => one.id === unitId)

  /*
   * What the person already has where this grant would apply. The sentence it
   * produces is the one thing that stops a well-meant grant from being a
   * no-op — "Priya already has Viewer at Keystone Grounds" — and it is read
   * from the same `permissionsAt` the server enforces with.
   */
  const ancestorsOf = useCallback((id: string) => {
    const byId = new Map(units.map((one) => [one.id, one]))
    const trail: string[] = []
    let at = byId.get(id)?.parentId ?? null
    while (at) { trail.push(at); at = byId.get(at)?.parentId ?? null }
    return trail
  }, [units])

  const mineHere = unitId
    ? permissionsAt(me.grants, ancestorsOf, unitId)
    : null

  async function grant() {
    if (!who || !roleId || !unitId) return
    setBusy(true)
    setError(null)
    try {
      await request(`/people/${who.id}/grants`, {
        method: 'POST',
        body: { orgUnitId: unitId, roleId, note },
      })
      onGranted()
    } catch (problem) {
      const body = problem as { field?: string; message?: string }
      setError({ field: body.field, message: body.message ?? 'That grant did not save.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Grant access"
      width={520}
      footerNote={who ? `${who.name} gets an email about the change.` : undefined}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || !who || !roleId || !unitId}
            onClick={() => void grant()}
          >
            {busy ? 'Granting…' : 'Grant'}
          </Button>
        </>
      )}
    >
      <div className="flex flex-col gap-3">
        <span className="text-[12.5px] text-text-muted">A person, a role, and where it applies</span>

        <Field label="Person" required hint="or invite someone new under Users & roles">
          {({ id, describedBy }) => (
            <ComboInput
              id={id}
              describedBy={describedBy}
              label="Person"
              thing="person"
              disabled={Boolean(person)}
              value={who}
              onPick={setWho}
              search={searchPeople}
            />
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Role"
            required
            hint={headline(roles.find((one) => one.id === roleId))}
            error={error?.field === 'roleId' ? error.message : undefined}
          >
            {({ id, describedBy, invalid }) => (
              <SelectInput
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                label="Role"
                value={roleId}
                onChange={setRoleId}
                options={roles.map((one) => ({ value: one.id, label: one.name }))}
              />
            )}
          </Field>

          <Field
            label="At"
            required
            hint="company, region, district or branch"
            error={error?.field === 'orgUnitId' ? error.message : undefined}
          >
            {({ id, describedBy, invalid }) => (
              <SelectInput
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                label="At"
                value={unitId}
                onChange={setUnitId}
                options={units.map((one) => ({ value: one.id, label: one.name }))}
              />
            )}
          </Field>
        </div>

        {unit && (
          <Band tone="green" title="Reaches">
            <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Tag tone="ok">{unit.name}</Tag>
              {unit.counts.yards > 0 && (
                <Tag>{unit.counts.yards} yard{unit.counts.yards === 1 ? '' : 's'}</Tag>
              )}
              {unit.counts.crews > 0 && (
                <Tag>{unit.counts.crews} crew{unit.counts.crews === 1 ? '' : 's'}</Tag>
              )}
              {unit.counts.people > 0 && (
                <Tag>{unit.counts.people} {unit.counts.people === 1 ? 'person' : 'people'}</Tag>
              )}
            </span>
            {mineHere && !can(mineHere, 'company_settings', 'view') && (
              <span className="mt-1.5 block">
                You cannot reach {unit.name} yourself, so this grant will be refused.
              </span>
            )}
          </Band>
        )}

        <Field label="Note" hint="optional · shown in the audit log">
          {({ id }) => (
            <TextArea id={id} rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
          )}
        </Field>

        {error && !error.field && (
          <p className="m-0 text-[12.5px] text-danger">{error.message}</p>
        )}
      </div>
    </Modal>
  )
}

/** The role's three headline levels, said the way the mockup says them. */
function headline(role: RoleRow | undefined): string | undefined {
  if (!role?.permissions) return undefined
  return (['quotes', 'jobs', 'invoices'] as const)
    .map((area) => `${area}: ${role.permissions![area]}`)
    .join(' · ')
}

async function searchPeople(query: string): Promise<ComboItem[]> {
  const { items } = await request<{ items: { id: string; name: string; email: string }[] }>(
    `/people${query ? `?q=${encodeURIComponent(query)}` : ''}`)
  return items.map((one) => ({
    id: one.id,
    name: one.name,
    sub: one.email,
    lead: <Avatar name={one.name} size={22} />,
  }))
}

/** "Lehigh Valley" → LEVA; "York" → YORK. Two to six letters, uppercase. */
function suggestCode(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return ''
  const letters = words.length > 1
    ? words.map((word) => word.slice(0, 2)).join('')
    : words[0]!
  return letters.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 6)
}

/** "Keystone Grounds › Central PA" — the trail above a chosen parent. */
function pathOf(units: OrgUnitRow[], id: string): string {
  const byId = new Map(units.map((one) => [one.id, one]))
  const trail: string[] = []
  let at: string | null = id
  while (at) {
    const found: OrgUnitRow | undefined = byId.get(at)
    if (!found) break
    trail.unshift(found.name)
    at = found.parentId
  }
  return trail.join(' › ')
}
